// Anatomical body silhouette — Rule of Nines with anterior/posterior split.
// Front view = anterior surfaces (head-front, torso-front, arms-front,
// legs-front, groin). Back view = posterior surfaces (head-back, etc).
// Every A / P region is INDEPENDENTLY selectable.

import React from "react";
import { cn } from "@/lib/utils";

const STROKE = "stroke-stone-500";
const FILL_ON = "fill-sky-700";
const FILL_OFF = "fill-stone-100";

function Region({ d, active, onClick, testid, title }) {
  return (
    <path
      d={d}
      data-testid={testid}
      onClick={onClick}
      className={cn(
        STROKE,
        active ? FILL_ON : FILL_OFF,
        "transition-colors duration-100 cursor-pointer hover:fill-sky-500"
      )}
      strokeWidth="0.6"
      strokeLinejoin="round"
    >
      <title>{title}</title>
    </path>
  );
}

// Anatomical humanoid path definitions (viewBox 100 × 210).
// A stylised silhouette — head oval, neck, tapered torso, arms with subtle
// shoulder curve, legs with knee taper.
const P = {
  head:
    "M50 6 C40 6 34 14 34 24 C34 32 39 39 46 41 L46 44 L54 44 L54 41 C61 39 66 32 66 24 C66 14 60 6 50 6 Z",
  // Torso — trapezoidal chest tapering to waist
  torso:
    "M36 44 L64 44 L68 60 L67 84 L64 104 L36 104 L33 84 L32 60 Z",
  // Right (viewer left) arm — shoulder → biceps → forearm → hand
  armR:
    "M32 46 L28 60 L24 82 L22 108 L28 112 L30 108 L32 88 L34 70 L36 55 Z",
  // Left (viewer right) arm — mirror
  armL:
    "M68 46 L72 60 L76 82 L78 108 L72 112 L70 108 L68 88 L66 70 L64 55 Z",
  // Right leg (viewer left) — from hip, down through knee, calf, foot
  legR:
    "M36 104 L34 130 L34 158 L36 188 L44 196 L46 188 L46 158 L48 130 L48 104 Z",
  // Left leg (viewer right) — mirror
  legL:
    "M52 104 L52 130 L54 158 L54 188 L56 196 L64 188 L66 158 L66 130 L64 104 Z",
  // Groin — small trapezoid between torso base and legs (front only)
  groin: "M46 102 L54 102 L55 108 L45 108 Z",
};

export default function BodyDiagram({ selected, onToggle }) {
  // selected shape:
  //   head_f, head_b, torso_f, torso_b,
  //   larm_f, larm_b, rarm_f, rarm_b,
  //   lleg_f, lleg_b, rleg_f, rleg_b, groin

  const view = (side /* "f" | "b" */) => {
    const s = side; // suffix
    const isFront = side === "f";
    return (
      <svg
        viewBox="0 0 100 210"
        className="w-full h-auto"
        data-testid={`body-svg-${isFront ? "front" : "back"}`}
      >
        <Region d={P.head} active={selected[`head_${s}`]} onClick={() => onToggle(`head_${s}`)} testid={`body-region-head-${s}`} title={`Head ${isFront ? "front" : "back"} · 4.5%`} />
        <Region d={P.torso} active={selected[`torso_${s}`]} onClick={() => onToggle(`torso_${s}`)} testid={`body-region-torso-${s}`} title={`Torso ${isFront ? "anterior" : "posterior"} · 18%`} />
        {/* Arms — patient's LEFT is viewer's RIGHT on FRONT view, and viewer's LEFT on BACK view. */}
        <Region
          d={P.armR}
          active={isFront ? selected[`rarm_${s}`] : selected[`larm_${s}`]}
          onClick={() => onToggle(isFront ? `rarm_${s}` : `larm_${s}`)}
          testid={`body-region-arm-viewerleft-${s}`}
          title={`${isFront ? "Right (patient)" : "Left (patient)"} arm · 4.5%`}
        />
        <Region
          d={P.armL}
          active={isFront ? selected[`larm_${s}`] : selected[`rarm_${s}`]}
          onClick={() => onToggle(isFront ? `larm_${s}` : `rarm_${s}`)}
          testid={`body-region-arm-viewerright-${s}`}
          title={`${isFront ? "Left (patient)" : "Right (patient)"} arm · 4.5%`}
        />
        <Region
          d={P.legR}
          active={isFront ? selected[`rleg_${s}`] : selected[`lleg_${s}`]}
          onClick={() => onToggle(isFront ? `rleg_${s}` : `lleg_${s}`)}
          testid={`body-region-leg-viewerleft-${s}`}
          title={`${isFront ? "Right (patient)" : "Left (patient)"} leg · 9%`}
        />
        <Region
          d={P.legL}
          active={isFront ? selected[`lleg_${s}`] : selected[`rleg_${s}`]}
          onClick={() => onToggle(isFront ? `lleg_${s}` : `rleg_${s}`)}
          testid={`body-region-leg-viewerright-${s}`}
          title={`${isFront ? "Left (patient)" : "Right (patient)"} leg · 9%`}
        />
        {/* Groin — front only */}
        {isFront && (
          <Region d={P.groin} active={selected.groin} onClick={() => onToggle("groin")} testid="body-region-groin" title="Groin · 1%" />
        )}
        <text x="50" y="207" textAnchor="middle" className="fill-stone-500 font-mono text-[7px] uppercase tracking-widest">
          {isFront ? "Anterior" : "Posterior"}
        </text>
      </svg>
    );
  };

  return (
    <div className="border border-stone-200 bg-white rounded-md p-4" data-testid="body-diagram">
      <div className="grid grid-cols-2 gap-3">
        <div>{view("f")}</div>
        <div>{view("b")}</div>
      </div>
      <p className="mt-3 text-[11px] text-stone-500 leading-relaxed">
        Tap any region — front and back are counted independently (Rule of Nines with A/P split).
      </p>
    </div>
  );
}
